import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Alert,
  AlertIcon,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Text,
  FormErrorMessage,
  Select,
  Card,
  CardBody,
  CloseButton,
} from '@chakra-ui/react';

import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';

import { authRepository } from 'api/authRepository';
import { flattenZodErrors, AdduserPayload, AdduserSchema } from 'validations/Validations';
import { Organization, organizationRepository } from 'api/organizationRepository';

function AddUser() {

  const role = localStorage.getItem('role') as keyof typeof roleOptions | null;
  const navigate = useNavigate();

  const [show, setShow] = useState(false);
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState('');
  const [centerAlertStatus, setCenterAlertStatus] = useState<'success' | 'error'>('success');
  const [plant, setPlant] = useState<Organization[]>([]);
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [orgName, setOrgName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] =
    useState<Partial<Record<keyof AdduserPayload, string>>>({});

  const roleOptions = {
    Admin: [
      'Admin',
      'OrgAdmin',
      'LocationAdmin',
      'DeptUser',
      'Vendor',
      'VendorUser',
    ],
    OrgAdmin: ['LocationAdmin', 'DeptUser'],
    LocationAdmin: ['DeptUser'],
  };

  const availableRoles = roleOptions[role] || [];

  const showAlert = (message: string, status: 'success' | 'error') => {
    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);

    setTimeout(() => {
      setCenterAlertVisible(false);
    }, 2000);
  };

  const [formData, setFormData] = useState<AdduserPayload>({
    firstName: '',
    lastName: '',
    email: '',
    passwordHash: '',
    userRole: '',
    contactNumber: '',
  });


  const handleFieldChange = (field: keyof AdduserPayload, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    const fieldSchema = AdduserSchema.shape[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };

  const handlePlantChange = (field: string, value: string) => {
    setLocation(value);

    setDepartment("");

    setFormData((prev) => ({
      ...prev,
      [field]: value,
      departmentId: "",
    }));
  };
  const handleDepartmentChange = (field: string, value: string) => {
    setDepartment(value);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const selectedLocation = plant
    .flatMap((org: any) => org.locations || [])
    .find((loc: any) => loc.locationId === location);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const payload = {
      ...formData,
    };

    // OrgAdmin creating users
    if (role === "OrgAdmin") {
      payload.OrgId = localStorage.getItem("orgId") || "";
      payload.LocationId = location;
      payload.DepartmentId = department;
      payload.DepartmentName = selectedLocation?.departments?.find((d: any) => d.departmentId === department)?.departmentName || "";
      payload.Location = location;
      payload.OrgName =
        plant[0]?.orgName ||
        plant[0]?.name ||
        "";
    }

    // LocationAdmin creating DeptUser

    if (role === "LocationAdmin") {
      payload.OrgId = localStorage.getItem("orgId") || "";
      payload.LocationId = localStorage.getItem("locationId") || "";
      payload.DepartmentId = department;
      payload.DepartmentName = selectedLocation?.departments?.find((d: any) => d.departmentId === department)?.departmentName || "";
      payload.Location = location;
      payload.OrgName =
        plant[0]?.orgName ||
        plant[0]?.name ||
        "";
    }
    const result = AdduserSchema.safeParse(payload);

    if (!result.success) {
      setErrors(flattenZodErrors(result.error));
      showAlert("Please fix highlighted errors.", "error");
      setIsSubmitting(false);
      return;
    }

    try {
      await authRepository.createUser(result.data);

      showAlert("Add new user Successfully", "success");

      setTimeout(() => {
        navigate("/admin/UserList");
      }, 2000);
    } catch {
      showAlert(
        "Email already exists. Please use a different email.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (role === "OrgAdmin") {
      handleFieldChange("userRole", "LocationAdmin");
    } else if (role === "LocationAdmin") {
      handleFieldChange("userRole", "DeptUser");
    }

    const getOrg = async () => {
      if (role === "OrgAdmin" || role === "LocationAdmin") {
        const orgId = localStorage.getItem("orgId");
        const locationId = localStorage.getItem("locationId");

        if (orgId || locationId) {
          try {
            const response =
              await organizationRepository.getOrganizationById(orgId);

            setPlant([response]);
            setOrgName(response.orgName || "");
            handlePlantChange(
              "locationId",
              response?.locations?.[0]?.locationId || ""
            );
            handleDepartmentChange(
              "departmentId",
              response?.locations?.[0]?.departments?.[0]?.departmentId || ""
            );
          } catch (error) {
          }
        }
      }
    };
    getOrg();
  }, [role]);

  return (
    <Flex
      position="relative"
      direction="column"
      align="center"
      justify="center"
      w="100%"
      minH="100vh"
      pt="80px"
      px="20px"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="50vh"
        bg="blue.600" bgGradient="linear(180deg, #6A5BFF 0%, #2B18FF 100%)"
        borderRadius="0 0 20px 20px"
        zIndex={0}
      />

      {/* Center Alert */}

      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="lg"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />

            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}

      {/* Card Wrapper */}
      <Card
        w={{ base: '100%', md: '500px' }}
        p="20px"
        borderRadius="20px"
        boxShadow="lg"
        zIndex={1}
      >
        <CardBody>
          <Heading color="navy.700 || white" fontSize="25px" mb="5px">
            New User
          </Heading>

          <Text mb="30px" color="gray.500" fontSize="md">
            Enter User details to create an User!
          </Text>
          <Box>
            <CloseButton
              position="absolute"
              top="15px"
              right="15px"
              size="lg"
              onClick={() => navigate(-1)}
            />
          </Box>
          {/* Name Fields */}
          <FormControl isInvalid={!!errors.firstName || !!errors.lastName}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
              <Flex direction="column">
                <FormLabel fontSize="sm" fontWeight="500" color="navy.700 || white">
                  First Name<Text color="brand.500"></Text>
                </FormLabel>
                <Input
                  variant="auth"
                  size="md"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleFieldChange('firstName', e.target.value)
                  }
                />
                {errors.firstName && (
                  <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                )}
              </Flex>

              <Flex direction="column">
                <FormLabel fontSize="sm" fontWeight="500" color="navy.700 || white">
                  Last Name<Text color="brand.500"></Text>
                </FormLabel>
                <Input
                  variant="auth"
                  size="md"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleFieldChange('lastName', e.target.value)
                  }
                />
                {errors.lastName && (
                  <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                )}
              </Flex>
            </SimpleGrid>
          </FormControl>

          {/* Email */}
          <FormControl mt="20px" isInvalid={!!errors.email}>
            <FormLabel fontSize="sm" color="navy.700 || white">
              Email<Text color="brand.500"></Text>
            </FormLabel>
            <Input
              variant="auth"
              type="email"
              size="md"
              placeholder="mail@example.com"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
            />
            {errors.email && (
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            )}
          </FormControl>

          {/* contact */}
          <FormControl mt="20px" isInvalid={!!errors.contactNumber}>
            <FormLabel fontSize="sm" color="navy.700 || white">
              Contact Number<Text color="brand.500"></Text>
            </FormLabel>
            <Input
              variant="auth"
              size="md"
              placeholder="+91"
              value={formData.contactNumber}
              onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
            />
            {errors.contactNumber && (
              <FormErrorMessage>{errors.contactNumber}</FormErrorMessage>
            )}
          </FormControl>


          {/* Role */}
          <FormControl mt="20px" isInvalid={!!errors.userRole}>
            <FormLabel fontSize="sm" color="navy.700 || white">
              Role
            </FormLabel>

          <Select
  variant="auth"
  size="md"
  placeholder="Select your role"
  value={formData.userRole}
  onChange={(e) => handleFieldChange("userRole", e.target.value)}
  color="white"         
>
  {availableRoles.map((roleOption: any) => (
    <option
      key={roleOption}
      value={roleOption}
      style={{
        backgroundColor: "#2D3748",
        color: "white",
      }}
    >
      {roleOption}
    </option>
  ))}
</Select>

            {errors.userRole && (
              <FormErrorMessage>{errors.userRole}</FormErrorMessage>
            )}
          </FormControl>

          {role === "OrgAdmin" &&
            (formData.userRole === "LocationAdmin" ||
              formData.userRole === "DeptUser") && (
              <FormControl mt="20px">
                <FormLabel>Select Plant</FormLabel>

                <Select
                  placeholder="Select Plant"
                  value={location}
                  onChange={(e) =>
                    handlePlantChange("locationId", e.target.value)
                  }
                >
                  {plant.flatMap((org: any) =>
                    org.locations?.map((loc: any) => (
                      <option
                        key={loc.locationId}
                        value={loc.locationId}
                      >
                        {loc.name}
                      </option>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

          {((role === "OrgAdmin" &&
            formData.userRole === "DeptUser") ||
            (role === "LocationAdmin" &&
              formData.userRole === "DeptUser")) && (
              <FormControl mt="20px">
                <FormLabel>
                  Select Department<Text color="brand.500"></Text>
                </FormLabel>

                <Select
                  placeholder="Select Department"
                  value={department}
                  onChange={(e) =>
                    handleDepartmentChange(
                      "departmentId",
                      e.target.value
                    )
                  }
                >
                  {selectedLocation?.departments?.map((dept: any) => (
                    <option
                      key={dept.departmentId}
                      value={dept.departmentId}
                    >
                      {dept.departmentName}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          {/* Password */}
          <FormControl mt="20px" isInvalid={!!errors.passwordHash}>
            <FormLabel fontSize="sm" color="navy.700 || white">
              Password<Text color="brand.500"></Text>
            </FormLabel>

            <InputGroup size="md">
              <Input
                variant="auth"
                type={show ? 'text' : 'password'}
                size="md"
                placeholder="Min. 8 characters"
                value={formData.passwordHash}
                onChange={(e) =>
                  handleFieldChange('passwordHash', e.target.value)
                }
              />

              <InputRightElement>
                <Icon
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={() => setShow(!show)}
                  cursor="pointer"
                  color="gray.500"
                />
              </InputRightElement>
            </InputGroup>

            {errors.passwordHash && (
              <FormErrorMessage>{errors.passwordHash}</FormErrorMessage>
            )}
          </FormControl>

          <Button
            mt="24px"
            variant="brand"
            w="100%"
            h="50px"
            fontSize="14px"
            onClick={handleSubmit}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
          >
            Create New User
          </Button>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default AddUser;
